import React, { useRef } from "react";
import { defineMessages } from "react-intl";
import { Select, ListSubheader, MenuItem, Divider } from "@mui/material";
import { v4 } from "uuid";
import "../input/validation.js";
import "../input/DatePicker.js";
import "../input/DateRangePicker.js";
import { StyledInput } from "../input/StyledInput.js";
import "../input/FormInput.js";
import "../input/StyledFormInputHoc.js";
import "moment-timezone";
import "../input/DateSinglePicker.localize.js";
import "../input/hooks.js";
import { useMultiSelect, useSearch } from "./hooks.js";
import { SelectFooter } from "./MultiSelectFooter.js";
import { MultiSelectItem } from "./MultiSelectItem.js";
import { MultiSelectWithTextCounterValue } from "./MultiSelectWithTextCounterValue.js";
import { SelectArrowIcon } from "./SelectArrowIcon.js";
import { useFilterSelectClasses, useGeneralSelectClasses } from "./selectStyle.js";
import { doesInclude } from "./utils.js";
import { VirtualizedMultiSelectOptionList } from "./VirtualizedMultiSelectOptionList.js";
const localized = defineMessages({
  allSearchOptionLabel: {
    id: "MultiSelect_allSearchOptionLabel",
    defaultMessage: "Select All"
  }
});
const selectAllOptionValue = "all";
const MultiSelect = ({
  options,
  value,
  emptyValue,
  onSubmit,
  enumFormatter,
  label,
  chipType,
  ChipComponent,
  showResetButton,
  horizontalPosition,
  hasSearch,
  iconLabel,
  mode = "client",
  onFilterChange,
  showPagination,
  page,
  pageSize,
  total,
  onPageChange,
  searchPlaceholder,
  hasSelectAll,
  renderValue,
  onSelectAll,
  defaultSelectAll,
  disabled,
  showApplyButton,
  onApply,
  virtualization,
  popoverClassName,
  fullWidthInput
}) => {
  const maxVisibleOptionsCount = 6;
  const { classes, cx } = useFilterSelectClasses();
  const { classes: generalSelectClasses } = useGeneralSelectClasses();
  const { current: multiselectId } = useRef(v4());
  const {
    selectAll,
    selectOpen,
    selectedValue,
    expandValue,
    handleApply,
    handleChange,
    handleClearSelection,
    handleClose,
    handleFilterChange,
    handleItemClick,
    handleOpen,
    handleSelectAll,
    handleExpand,
    handleCollapse
  } = useMultiSelect({
    defaultSelectAll,
    mode,
    options,
    value,
    onApply,
    onPageChange,
    onSelectAll,
    onSubmit
  });
  const { inputValue, visibleOptions, handleSearchChange } = useSearch({
    mode,
    onFilterChange,
    options
  });
  function getInputValue(selected) {
    return renderValue ? renderValue(selected) : /* @__PURE__ */ React.createElement(
      MultiSelectWithTextCounterValue,
      {
        selected,
        enumFormatter,
        label,
        options,
        emptyValue
      }
    );
  }
  return /* @__PURE__ */ React.createElement(
    Select,
    {
      disabled,
      displayEmpty: true,
      value: selectedValue,
      multiple: true,
      onChange: handleChange,
      onClose: handleClose,
      onOpen: handleOpen,
      open: selectOpen,
      renderValue: iconLabel ? () => iconLabel : getInputValue,
      MenuProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: horizontalPosition
        },
        transformOrigin: {
          vertical: "top",
          horizontal: horizontalPosition
        },
        PopoverClasses: {
          paper: cx(classes.selectPopover, popoverClassName)
        },
        autoFocus: false
      },
      IconComponent: () => iconLabel ? null : /* @__PURE__ */ React.createElement(SelectArrowIcon, { selectOpen }),
      className: cx(classes.selectInput, {
        [classes.selectInputIconLabel]: !!iconLabel,
        [classes.selectInputFullWidth]: fullWidthInput
      }),
      disableUnderline: true,
      "data-testid": "multiselect",
      inputProps: { "data-testid": "multiSelectInput" }
    },
    hasSearch ? /* @__PURE__ */ React.createElement(ListSubheader, { disableGutters: true }, /* @__PURE__ */ React.createElement(
      StyledInput,
      {
        value: inputValue,
        className: classes.selectSearch,
        onKeyDown: (e) => e.stopPropagation(),
        onChange: handleSearchChange,
        placeholder: searchPlaceholder
      }
    )) : null,
    /* @__PURE__ */ React.createElement(MenuItem, { value: "", className: generalSelectClasses.selectItemHidden }),
    hasSelectAll && !inputValue ? /* @__PURE__ */ React.createElement(
      MultiSelectItem,
      {
        key: `multiselect-${multiselectId}-${selectAllOptionValue}`,
        value: selectAllOptionValue,
        label: localized.allSearchOptionLabel,
        isSelected: selectAll,
        onClick: handleSelectAll,
        chipType
      }
    ) : null,
    /* @__PURE__ */ React.createElement(Divider, { className: generalSelectClasses.divider }),
    virtualization ? /* @__PURE__ */ React.createElement(
      VirtualizedMultiSelectOptionList,
      {
        items: visibleOptions,
        multiselectId,
        selectedValue,
        chipType,
        onItemClick: handleItemClick,
        visibleOptionCount: visibleOptions?.length > maxVisibleOptionsCount ? maxVisibleOptionsCount : visibleOptions?.length,
        expandValue,
        onExpand: handleExpand,
        onCollapse: handleCollapse
      }
    ) : visibleOptions?.map(({ value: value2, label: label2, subOptions, autoSelectOnExpand, chipValue }) => {
      const hasSubOptions = subOptions?.length > 0;
      const isExpanded = doesInclude(expandValue, value2.toString());
      return [
        /* @__PURE__ */ React.createElement(
          MultiSelectItem,
          {
            key: `multiselect-${multiselectId}-${value2}-${v4()}`,
            value: value2,
            chipValue: chipValue ?? value2,
            label: label2,
            isSelected: hasSubOptions ? subOptions?.every((o) => doesInclude(selectedValue, o?.value)) : doesInclude(selectedValue, value2),
            chipType: !autoSelectOnExpand ? chipType : void 0,
            ChipComponent,
            hasSuboptions: hasSubOptions,
            isExpanded,
            onExpand: () => handleExpand(value2),
            onCollapse: () => handleCollapse(value2)
          }
        ),
        ...hasSubOptions && isExpanded ? subOptions.map((i, index) => /* @__PURE__ */ React.createElement(
          MultiSelectItem,
          {
            key: `multiselect-${multiselectId}-${i.value}-${v4()}`,
            value: i.value,
            label: i.label,
            isSelected: doesInclude(selectedValue, i.value),
            suboption: true,
            divider: index === subOptions.length - 1,
            checkbox: !autoSelectOnExpand
          }
        )) : []
      ];
    }),
    /* @__PURE__ */ React.createElement(
      SelectFooter,
      {
        showResetButton,
        onClear: handleClearSelection,
        showPagination,
        page,
        pageSize,
        total,
        onPageChange: handleFilterChange,
        showApplyButton,
        onApply: handleApply
      }
    )
  );
};
export {
  MultiSelect,
  selectAllOptionValue
};
//# sourceMappingURL=MultiSelect.js.map
